import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './LandingPage.css';

const DECLARATIONS_URL = 'https://tltd-cgm.codeiums.com/';

const MODULES = [
  {
    id: 'caisse',
    category: 'CAISSE',
    name: 'Caisse CGM',
    subtitle: 'Encaissements & décaissements',
    description:
      'Application caisse du cabinet : mouvements, soldes et suivi en temps réel. Connectez-vous pour y accéder.',
    features: ['Encaissements clients', 'Décaissements cabinet', 'Soldes à jour'],
    audience: 'Équipe caisse & administration',
    pills: ['Login Caisse', 'Clients', 'Mouvements'],
    cta: 'Ouvrir le login Caisse',
    action: 'login',
    intent: 'caisse',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
        <rect x="2.5" y="6" width="19" height="13" rx="1.8" />
        <path d="M2.5 10.5h19" />
      </svg>
    ),
  },
  {
    id: 'honoraires',
    category: 'NOTES  D\'HONORAIRES',
    name: 'Notes d’honoraires',
    subtitle: 'Facturation NH du cabinet',
    description:
      'Module NH : création, suivi et export des notes d’honoraires. Connectez-vous pour ouvrir l’application.',
    features: ['Numérotation NH', 'Factures multi-cabinets', 'Export PDF'],
    audience: 'Experts-comptables & assistants',
    pills: ['Login NH', 'Facturation', 'PDF'],
    cta: 'Ouvrir le login NH',
    action: 'login',
    intent: 'honoraires',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
        <rect x="4" y="3" width="16" height="18" rx="1.6" />
        <path d="M8 8h8M8 12h8M8 16h5" />
      </svg>
    ),
  },
  {
    id: 'declarations',
    category: 'DÉCLARATIONS',
    name: 'Déclarations',
    subtitle: 'Espace déclaratif fiscal & social',
    description:
      'Ouvre la page de déclarations (application dédiée) pour les échéances fiscales et sociales.',
    features: ['Échéances fiscales', 'Suivi dossiers', 'Portail déclaratif'],
    audience: 'Cabinet & collaborateurs déclarations',
    pills: ['Page déclarations', 'Fiscal', 'Social'],
    cta: 'Ouvrir les déclarations',
    action: 'external',
    href: DECLARATIONS_URL,
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3.5 2" />
      </svg>
    ),
  },
  {
    id: 'paie',
    category: 'PAIE',
    name: 'Paie & personnel',
    subtitle: 'Bientôt disponible',
    description:
      'Module paie en préparation. Bulletins et charges du personnel arriveront prochainement.',
    features: ['Bulletins (à venir)', 'Charges sociales (à venir)', 'Exports (à venir)'],
    audience: 'RH & administration — bientôt',
    pills: ['Vide pour le moment'],
    cta: 'Bientôt disponible',
    action: 'disabled',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
        <circle cx="12" cy="8" r="3.6" />
        <path d="M4.5 20c1.4-4 4-6 7.5-6s6.1 2 7.5 6" />
      </svg>
    ),
  },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [loginForm, setLoginForm] = useState({
    username: '',
    password: '',
  });

  useEffect(() => {
    const nodes = document.querySelectorAll('.cgm-landing__reveal');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    nodes.forEach((el, i) => {
      el.style.transitionDelay = `${(i % 6) * 0.06}s`;
      observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const handleLoginChange = (e) => {
    const { name, value } = e.target;
    setLoginForm((prev) => ({ ...prev, [name]: value }));
  };

  const focusLogin = () => {
    const el = document.getElementById('landing-username');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.focus();
    }
  };

  const openModule = (mod) => {
    if (mod.action === 'disabled') return;
    if (mod.action === 'external' && mod.href) {
      window.open(mod.href, '_blank', 'noopener,noreferrer');
      return;
    }
    if (mod.action === 'login') {
      try {
        if (mod.intent) sessionStorage.setItem('pendingModule', mod.intent);
      } catch (_) {
        /* ignore */
      }
      focusLogin();
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (!loginForm.username || !loginForm.password) {
      setError("Nom d'utilisateur et mot de passe requis");
      setLoading(false);
      return;
    }

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 20000);

      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: loginForm.username.trim(),
          password: loginForm.password,
        }),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);
      const data = await response.json();

      if (data.success) {
        login({
          ...data.user,
          loginMethod: 'username',
          token: data.token,
        });
        navigate('/cabinets', { replace: true });
      } else {
        setError(data.error || "Nom d'utilisateur ou mot de passe incorrect");
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        setError('Délai dépassé — MySQL est peut-être occupé. Réessayez.');
      } else {
        setError('Erreur de connexion au serveur');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="cgm-landing">
      <div className="cgm-landing__grain" aria-hidden="true" />

      <a
        href="https://ccgm.com.tn/"
        target="_blank"
        rel="noopener noreferrer"
        className="cgm-landing__topbar"
      >
        <span className="cgm-landing__topbar-text">
          Pour accéder à notre site web officiel, accédez ici
        </span>
        <span className="cgm-landing__topbar-cta">
          ccgm.com.tn
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true">
            <path d="M5 12h14" />
            <path d="M13 6l6 6-6 6" />
          </svg>
        </span>
      </a>

      <header className="cgm-landing__nav">
        <div className="cgm-landing__nav-inner">
          <Link to="/" className="cgm-landing__brand" aria-label="CCGM accueil">
            <img src="/logo.png" alt="" className="cgm-landing__brand-logo" />
            <span className="cgm-landing__brand-name">CCGM</span>
          </Link>
          <nav className="cgm-landing__links" aria-label="Navigation">
            <a href="#connexion">Connexion</a>
            <a href="#modules">Modules</a>
            <a href="#comment">Comment ça marche</a>
            <a href="https://ccgm.com.tn/" target="_blank" rel="noopener noreferrer">
              Contact
            </a>
          </nav>
          <div className="cgm-landing__nav-right">
    
            <a href="#connexion" className="cgm-landing__btn cgm-landing__btn--solid">
              Connexion
            </a>
          </div>
        </div>
      </header>

      <section className="cgm-landing__hero" id="connexion">
        <div className="cgm-landing__wrap cgm-landing__hero-grid">
          <div className="cgm-landing__hero-copy">
            <div className="cgm-landing__badge cgm-landing__reveal">
              <span className="cgm-landing__badge-dot" />
              CCGM
            </div>
            <h1 className="cgm-landing__reveal">
              La comptabilité, <span className="accent">enfin claire</span>
            </h1>
            <p className="cgm-landing__lead cgm-landing__reveal">
              Connectez-vous avec vos identifiants pour accéder aux modules.
            </p>
            <div className="cgm-landing__pills cgm-landing__reveal">
              <span className="cgm-landing__pill">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2v20M2 12h20" />
                </svg>
                4 modules connectés
              </span>
              <span className="cgm-landing__pill">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="9" />
                  <path d="M12 7v5l3 2" />
                </svg>
                Données à jour en direct
              </span>
              <span className="cgm-landing__pill">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M5 13l4 4L19 7" />
                </svg>
                Expertise cabinet
              </span>
            </div>
          </div>

          <div className="cgm-landing__login-card cgm-landing__reveal" aria-label="Connexion">
            <form className="cgm-landing__login-form" onSubmit={handleLogin} noValidate>
              <h2>Connexion</h2>

              {error ? (
                <div className="cgm-landing__login-error" role="alert">
                  {error}
                </div>
              ) : null}

              <label className="cgm-landing__field" htmlFor="landing-username">
                <span className="cgm-landing__sr-only">Nom d&apos;utilisateur</span>
                <input
                  id="landing-username"
                  name="username"
                  type="text"
                  autoComplete="username"
                  placeholder="Nom d'utilisateur"
                  value={loginForm.username}
                  onChange={handleLoginChange}
                  disabled={loading}
                />
              </label>
              <label className="cgm-landing__field" htmlFor="landing-password">
                <span className="cgm-landing__sr-only">Mot de passe</span>
                <input
                  id="landing-password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  placeholder="Mot de passe"
                  value={loginForm.password}
                  onChange={handleLoginChange}
                  disabled={loading}
                />
              </label>
              <button type="submit" className="cgm-landing__login-submit" disabled={loading}>
                {loading ? 'Connexion…' : 'Se connecter'}
              </button>
            </form>
          </div>
        </div>
      </section>

      <section className="cgm-landing__section" id="comment">
        <div className="cgm-landing__wrap">
          <div className="cgm-landing__section-head cgm-landing__reveal">
            <div className="cgm-landing__eyebrow">Comment ça marche</div>
            <h2>Trois étapes pour démarrer.</h2>
            <p>Connectez-vous, choisissez le cabinet, ouvrez le module dont vous avez besoin.</p>
          </div>
          <div className="cgm-landing__steps">
            <div className="cgm-landing__step cgm-landing__reveal">
              <span className="cgm-landing__step-num">01</span>
              <h3>Connectez-vous</h3>
              <p>Identifiants du cabinet — même compte pour tous les modules autorisés.</p>
            </div>
            <div className="cgm-landing__step cgm-landing__reveal">
              <span className="cgm-landing__step-num">02</span>
              <h3>Choisissez le cabinet</h3>
              <p>CGM1, CGM2 ou autre accès selon vos droits.</p>
            </div>
            <div className="cgm-landing__step cgm-landing__reveal">
              <span className="cgm-landing__step-num">03</span>
              <h3>Ouvrez un module</h3>
              <p>Caisse, NH, déclarations ou paie — selon vos droits.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="cgm-landing__section" id="modules">
        <div className="cgm-landing__wrap">
          <div className="cgm-landing__section-head cgm-landing__reveal">
            <div className="cgm-landing__eyebrow">Applications reliées</div>
            <h2>Nos modules métier.</h2>
            <p>
              Caisse et NH ouvrent le login de l’application. Les déclarations ouvrent leur page dédiée.
              La paie arrive bientôt.
            </p>
          </div>
          <div className="cgm-landing__modules">
            {MODULES.map((mod) => {
              const disabled = mod.action === 'disabled';
              return (
                <button
                  key={mod.id}
                  type="button"
                  className={`cgm-sol-card cgm-sol-card--${mod.id} cgm-landing__reveal${disabled ? ' is-disabled' : ''}`}
                  onClick={() => openModule(mod)}
                  disabled={disabled}
                  aria-label={mod.name}
                >
                  <div className="cgm-sol-card__banner">
                    <span className="cgm-sol-card__banner-icon" aria-hidden="true">
                      {mod.icon}
                    </span>
                    <span className="cgm-sol-card__banner-cat">{mod.category}</span>
                  </div>
                  <div className="cgm-sol-card__body">
                    <h3>{mod.name}</h3>
                    <p className="cgm-sol-card__subtitle">{mod.subtitle}</p>
                    <p className="cgm-sol-card__desc">{mod.description}</p>
                    <ul className="cgm-sol-card__features">
                      {mod.features.map((f) => (
                        <li key={f}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden="true">
                            <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                          {f}
                        </li>
                      ))}
                    </ul>
                    <p className="cgm-sol-card__audience">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                        <circle cx="12" cy="8" r="3.2" />
                        <path d="M5 19c1.2-3.2 3.4-4.8 7-4.8s5.8 1.6 7 4.8" />
                      </svg>
                      {mod.audience}
                    </p>
                    <div className="cgm-sol-card__pills">
                      {mod.pills.map((p) => (
                        <span key={p}>{p}</span>
                      ))}
                    </div>
                    <span className="cgm-sol-card__cta">
                      {mod.cta}
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true">
                        <path d="M5 12h14" />
                        <path d="M13 6l6 6-6 6" />
                      </svg>
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      <section className="cgm-landing__final" id="contact">
        <div className="cgm-landing__wrap cgm-landing__reveal">
          <div className="cgm-landing__eyebrow">Besoin d’aide ?</div>
          <h2>Votre bilan pourrait déjà être à jour.</h2>
          <div className="cgm-landing__ctas" style={{ justifyContent: 'center', marginBottom: 16 }}>
            <a href="#connexion" className="cgm-landing__btn cgm-landing__btn--solid cgm-landing__btn--lg">
              Aller à la connexion
            </a>
            <a
              href="https://ccgm.com.tn/"
              target="_blank"
              rel="noopener noreferrer"
              className="cgm-landing__btn cgm-landing__btn--outline cgm-landing__btn--lg"
            >
              Pour accéder à notre site web officiel, accédez ici
            </a>
          </div>
          <p className="cgm-landing__hero-note">(+216) 52 959 655 · contact@ccgm.com.tn</p>
        </div>
      </section>

      <footer className="cgm-landing__footer">
        <div className="cgm-landing__wrap cgm-landing__footer-inner">
          <div className="cgm-landing__brand">
            <img src="/logo.png" alt="" className="cgm-landing__brand-logo" />
            <span className="cgm-landing__brand-name">CCGM</span>
          </div>
          <div className="cgm-landing__footer-links">
            <a href="#connexion">Connexion</a>
            <a href="#modules">Modules</a>
            <a href="#comment">Comment ça marche</a>
            <a href="https://ccgm.com.tn/" target="_blank" rel="noopener noreferrer">
              Contact
            </a>
          </div>
          <div className="cgm-landing__footer-copy">
            © {new Date().getFullYear()} CCGM — Cabinet Ghoufa Mohamed
          </div>
        </div>
      </footer>
    </div>
  );
}
